// src/components/intrare/List.jsx
import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Alert,
  Spinner,
  Badge,
  Form,
  InputGroup,
} from "react-bootstrap";
import ReactPaginate from "react-paginate";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { authFetch } from "../utils/authFetch"; // fetch JSON

const toYMD = (d) => format(d, "yyyy-MM-dd");

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

function SortHeader({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "↕";
  return (
    <th
      role="button"
      className="user-select-none"
      onClick={() => onSort(col)}
      title={`Sort by ${label}`}
    >
      <span className="me-1">{label}</span>
      <span style={{ opacity: 0.6, fontSize: 12 }}>{arrow}</span>
    </th>
  );
}

export default function IntrareList() {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // filtre
  const [searchClient, setSearchClient] = useState("");
  const [searchDocNo, setSearchDocNo] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // sort
  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("desc");

  // paginate
  const [currentPage, setCurrentPage] = useState(0); // 0-based pentru ReactPaginate
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const onSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const { account_id } = useParams();
  const [searchParams] = useSearchParams();
  const bxsoldint = searchParams.get("bxsoldint");
  const accountId = Number(account_id || searchParams.get("account_id") || "");

  // Reset pagina la schimbarea filtrelor/sortării/mărimii paginii
  useEffect(() => {
    setCurrentPage(0);
  }, [
    accountId,
    bxsoldint,
    searchClient,
    searchDocNo,
    startDate,
    endDate,
    sortBy,
    sortDir,
    itemsPerPage,
  ]);

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      // o singură dată selectată => exact acea zi
      let start = startDate,
        end = endDate;
      if (start && end && start > end) throw new Error("Data de început trebuie să fie înaintea datei de sfârșit.");

      const skip = currentPage * itemsPerPage;

      const usp = new URLSearchParams();
      if (searchClient?.trim()) usp.set("client", searchClient.trim());
      if (searchDocNo?.trim()) usp.set("doc_no", searchDocNo.trim());
      if (start instanceof Date) usp.set("date_from", toYMD(start));
      if (end instanceof Date) usp.set("date_to", toYMD(end));
      usp.set("sort_by", sortBy);
      usp.set("sort_dir", sortDir);
      usp.set("skip", String(skip));
      usp.set("limit", String(itemsPerPage));
      if (bxsoldint === "1") {
        usp.set("bxsoldint", "1");
      }

      // const qs = usp.toString();
      // const data = await authFetch(`/intrare/${qs ? "?" + qs : ""}`);

      const qs = usp.toString();
      // const path = `/intrare${qs ? "?" + qs : ""}`;  // fără absolut, fără window.location
      // const data = await authFetch(path);

      //const res = await authFetch(`/intrare/${qs ? `?${qs}` : ""}`);

      // const data = await authFetch("/intrare");

      const path = accountId
        ? `/intrare/intrare_list/${accountId}${qs ? `?${qs}` : ""}`
        : `/intrare${qs ? `?${qs}` : ""}`;
      const data = await authFetch(path);
      // const items = Array.isArray(data)
      //   ? data
      //   : Array.isArray(data?.items)
      //     ? data.items
      //     : [];

      // if (!res.ok) {
      //   const msg = await res.text().catch(() => "");
      //   throw new Error(msg || `HTTP ${res.status}`);
      // }

      //const data = await res.json();

      // așteptat: { total, items }
      if (data && Array.isArray(data.items) && typeof data.total === "number") {
        setItems(data.items);
        setTotalCount(data.total);
      } else if (Array.isArray(data)) {
        // fallback: endpointul încă întoarce listă simplă
        setItems(data);
        setTotalCount(data.length); // nu știm totalul real
      } else {
        // fallback de siguranță
        setItems([]);
        setTotalCount(0);
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Sigur doriți să ștergeți intrarea cu ID ${id}?`)) {
      return;
    }
    try {
      await authFetch(`/intrare/${id}`, {
        method: "DELETE",
      });
      fetchList();
    } catch (e) {
      alert("Eroare la ștergere: " + (e?.message || e));
    }
  };

  const ctx = (() => {
    try {
      return JSON.parse(localStorage.getItem("intrare_ctx") || "null");
    } catch {
      return null;
    }
  })();

  const accountCode = ctx?.accountCode || "";
  const documentType = searchParams.get("tip_document") === "Aviz" ||
    (Number(ctx?.accountId) === accountId && String(ctx?.accountCode || "").startsWith("408"))
    ? "Aviz" : null;
  const accountLabel = ctx?.accountLabel || "";

  // inițial + când depindințele paginate/filtre/sort se schimbă
  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountId, // ✅ IMPORTANT
    bxsoldint,
    currentPage,
    itemsPerPage,
    searchClient,
    searchDocNo,
    startDate,
    endDate,
    sortBy,
    sortDir,
  ]);

  const pageCount = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const from = totalCount ? currentPage * itemsPerPage + 1 : 0;
  const to = Math.min(totalCount, (currentPage + 1) * itemsPerPage);

  return (
    <Container className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          {accountCode && <Badge bg="secondary">{accountCode}</Badge>}
          {accountLabel && <span className="text-muted">{accountLabel}</span>}
          <h3 className="mb-0">
            {bxsoldint === "1" ? "Facturi inițiale furnizori" : "Lista Facturi"}
          </h3>
        </Col>
        <Col xs="auto">
          <Button
            as={Link}
            to={(() => {
              const params = new URLSearchParams();
              if (accountId) params.set("account_id", String(accountId));
              if (bxsoldint === "1") params.set("bxsoldint", "1");
              if (documentType) params.set("tip_document", documentType);
              return `/intrare/new${params.size ? `?${params}` : ""}`;
            })()}
            variant="primary"
          >
              {documentType === "Aviz" ? "+ Adaugă Aviz" : "+ Adaugă Factură"}
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Caută după client"
              value={searchClient}
              onChange={(e) => setSearchClient(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") fetchList();
              }}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Caută după nr. doc"
              value={searchDocNo}
              onChange={(e) => setSearchDocNo(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <InputGroup>
            <InputGroup.Text>De la</InputGroup.Text>
            <DatePicker
              selected={startDate}
              onChange={setStartDate}
              dateFormat="dd.MM.yyyy"
              wrapperClassName="dp-w120"
              className="form-control"
              placeholderText="Start"
              isClearable
              selectsStart
              startDate={startDate}
              endDate={endDate}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <InputGroup>
            <InputGroup.Text>Până la</InputGroup.Text>
            <DatePicker
              selected={endDate}
              onChange={setEndDate}
              dateFormat="dd.MM.yyyy"
              wrapperClassName="dp-w120"
              className="form-control"
              placeholderText="End"
              isClearable
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate || undefined}
            />
          </InputGroup>
        </Col>

        <Col
          md={3}
          className="d-flex justify-content-end align-items-center gap-2"
        >
          <Form.Select
            size="sm"
            style={{ width: 110 }}
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            title="Rânduri pe pagină"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}/pag.
              </option>
            ))}
          </Form.Select>
          <Button variant="outline-primary" onClick={() => fetchList()}>
            Caută
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setSearchClient("");
              setSearchDocNo("");
              setStartDate(null);
              setEndDate(null);
            }}
          >
            Clear filtre
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          {totalCount ? `Afișez ${from}-${to} din ${totalCount}` : "—"}
        </small>
        <small className="text-muted">
          Sort: {sortBy} {sortDir}
        </small>
      </div>

      {loading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <SortHeader
                  label="ID"
                  col="id"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Doc No"
                  col="doc_no"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Data Doc"
                  col="doc_date"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Contact"
                  col="contact"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Valoare"
                  col="value"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Sold"
                  col="sold"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Linii"
                  col="lines"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <th className="text-end">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>{it.id}</td>
                  <td>{it.bxdoc_no || "-"}</td>
                  <td>{formatDateRo(it.bxdoc_date) || "-"}</td>
                  <td>{it.contact_name ?? it.contact_id}</td>
                  <td>{Number(it.bxgvalue || 0).toFixed(2)}</td>
                  <td>{Number(it.bxsold ?? 0).toFixed(2)}</td>
                  <td>{it.linii_count ?? it.linii?.length ?? 0}</td>
                  <td className="text-end">
                    <Button
                      as={Link}
                      to={`/intrare/${it.id}/edit`}
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                    >
                      Editează
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDelete(it.id)}
                    >
                      Șterge
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    Nu există înregistrări.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <ReactPaginate
            previousLabel={"← Înapoi"}
            nextLabel={"Înainte →"}
            pageCount={pageCount}
            forcePage={currentPage}
            onPageChange={(event) => setCurrentPage(event.selected)} // 0-based
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            activeClassName={"active"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            renderOnZeroPageCount={null}
          />
        </>
      )}
    </Container>
  );
}
